import { RocketOutlined } from '@ant-design/icons'
import { LinkWithQuery } from '@components/LinkWithQuery'
import { useCheckPermissions } from '@components/Permissions/useCheckPermissions'
import { DEFAULT_LANGUAGE } from '@constants'
import { NAME_KEY } from '@constants/fieldKeys'
import { getStringFromContent } from '@modules/content'
import {
  currentNetworkIdSelector,
  currentNetworkSelector,
  networksListSelector,
  networksSelectorConfigSelector,
} from '@store/slices/networks/selectors'
import { useAppSelector } from '@store/typedHooks'
import { permissions } from '@wdnsolutions/auth-helpers'
import { Dropdown } from 'antd'
import classNames from 'classnames'
import React, { memo, useMemo } from 'react'

import useTopSelectorChange from '../hooks/useChangeTopSelector'
import { IMenuItem } from '../SiteSelect'
import dropdownStyles from '../style.module.scss'
import styles from './style.module.scss'

const HIDDEN_NETWORK_IDS = new Set([
  'clvmgabul000008l4h7da5rj0',
  'clreq3z27000008l9c070cfdx',
  'clpsap0fy000008l6252g4r9d',
  'clvxjhxs4000008meet7rb96q',
  'clr0iq56x000009l68cz03yiq',
  'clwoonijy000008jz69iqcf01',
  'clwysohjh000008mhh5xkes1t',
  'clr9d5qq4000008l79a2a0xoc',
  'clx0hg0k9000009l4h5j2gkfn',
  'clrgaa7qn000008ia0r97clwk',
  'clsd5p2lx000008lb9qx8e1js',
  'clui99ry1000008l625qz8upa',
  'cmh3ddloc00001atohqc1dtm1',
  'cmh3de21d00001bsphs3wb4z9',
  'cmh3deib200001ats8eze1zxd',
  'cmh3dfrie00001attd6j2763c',
  'cmh3glsa900001atwf9g786d4',
])

const NetworkSelect = () => {
  const selectorConfig = useAppSelector(networksSelectorConfigSelector)
  const currentNetwork = useAppSelector(currentNetworkSelector)

  const menu = useRenderMenu()

  return (
    <Dropdown
      overlayClassName={dropdownStyles.dropdown}
      disabled={selectorConfig.disabled}
      menu={menu}
      trigger={['click']}
      placement="bottomLeft">
      <div
        className={classNames(
          dropdownStyles.dropdownButton,
          selectorConfig.disabled && dropdownStyles.disabled,
        )}>
        <i className={`${styles.icon} icmn-tree`} />
        {renderNetwork(currentNetwork)}
      </div>
    </Dropdown>
  )
}

export default memo(NetworkSelect)

function renderNetwork(currentNetwork) {
  if (!currentNetwork) {
    return (
      <span className="font-weight-semibold text-uppercase text-white">
        All networks
      </span>
    )
  }

  return (
    <span className="font-weight-semibold text-uppercase text-white">
      {getStringFromContent(NAME_KEY, {
        content: currentNetwork?.content,
        language: DEFAULT_LANGUAGE.languageTag,
      }) || currentNetwork?.id}
    </span>
  )
}

function useRenderMenu() {
  const networks = useAppSelector(networksListSelector)
  const currentNetworkId = useAppSelector(currentNetworkIdSelector)
  const networkSelectorConfig = useAppSelector(networksSelectorConfigSelector)
  const { onTopSelectorChange } = useTopSelectorChange('network')

  const isAddingNetworkAllowed = useCheckPermissions([
    permissions.network.create,
  ])

  const visibleNetworks = useMemo(
    () => networks.filter(network => !HIDDEN_NETWORK_IDS.has(network.id)),
    [networks],
  )

  const menuItems = useMemo(() => {
    const menuItems: IMenuItem[] = [
      {
        key: 'all',
        label: 'All networks',
        disabled: networkSelectorConfig?.autoSet,
      },
      { key: 'divider1', type: 'divider' },
    ]

    visibleNetworks.forEach(network => {
      menuItems.push({
        key: network.id,
        label:
          getStringFromContent(NAME_KEY, {
            content: network?.content,
            language: DEFAULT_LANGUAGE.languageTag,
          }) || network.id,
      })
    })

    if (visibleNetworks.length) {
      menuItems.push({ key: 'divider2', type: 'divider' })
    }

    if (isAddingNetworkAllowed) {
      menuItems.push({
        key: 'add',
        label: (
          <LinkWithQuery to="/networks/add">
            <RocketOutlined className="mr-2" /> Add network
          </LinkWithQuery>
        ),
      })
    }

    return menuItems
  }, [isAddingNetworkAllowed, networkSelectorConfig, visibleNetworks])

  return {
    selectable: false,
    selectedKeys: [currentNetworkId || 'all'],
    onClick: onTopSelectorChange,
    items: menuItems,
  }
}
